// Prompts the user has generated with, per logged-in user, on this device. Kept
// in localStorage (the same backend-free posture as templatesStore.ts /
// apiKey.ts) so the autocomplete in the prompt field can offer them again in
// every later session.
//
// This is deliberately separate from the generation history in IndexedDB: a
// prompt is remembered the moment Generate is pressed, so the phrasing survives
// even when the run itself fails, and the list stays a cheap synchronous read
// the keystroke handler can filter without awaiting a database.

const STORAGE_PREFIX = 'assetLab.magicStudio.prompts.v1';

// How many prompts we keep per user. Well past what the 8-row popover can show,
// but small enough that the record stays a few KB of localStorage.
const MAX_PROMPTS = 100;

// Rows the autocomplete shows — the popover has no scroll, so this is also the
// number of options the user can navigate with the arrow keys.
export const PROMPT_SUGGEST_LIMIT = 8;

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

// The user's past prompts, most recent first. Empty when storage is
// unavailable (private mode) or holds nothing valid.
export function loadPrompts(userId: string): string[] {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .slice(0, MAX_PROMPTS);
  } catch {
    return [];
  }
}

// Record a prompt as the most recent one, returning the updated list so the
// caller can keep its in-memory copy in step. A repeat of an earlier prompt is
// moved back to the top rather than duplicated (matching is case-insensitive,
// keeping the casing the user just used). Best-effort: remembering prompts is a
// convenience, so a full / unavailable storage is silently tolerated.
export function rememberPrompt(userId: string, prompt: string): string[] {
  const text = prompt.trim();
  const current = loadPrompts(userId);
  if (!text) return current;
  const lower = text.toLowerCase();
  const next = [text, ...current.filter((p) => p.toLowerCase() !== lower)].slice(0, MAX_PROMPTS);
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    // ignore — nothing else we can do if storage is unavailable.
  }
  return next;
}

// What the autocomplete offers for the current field content: prompts that
// start with what's typed first, then the ones that merely contain it — both in
// most-recent-first order. An empty field simply lists the latest prompts.
export function suggestPrompts(
  prompts: string[],
  query: string,
  limit: number = PROMPT_SUGGEST_LIMIT,
): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return prompts.slice(0, limit);
  const starts: string[] = [];
  const contains: string[] = [];
  for (const p of prompts) {
    const lower = p.toLowerCase();
    // Already exactly what's in the field — there is nothing to complete.
    if (lower === q) continue;
    if (lower.startsWith(q)) starts.push(p);
    else if (lower.includes(q)) contains.push(p);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}
